import { useCallback, useState } from 'react';
import {
  isErpTaskSubmissionResponse,
  isTaskPollingCancelledError,
  postErpAsyncTask,
  waitForErpTasks
} from '@/lib/task-api';
import { useTaskPollingSignal } from '@/hooks/use-task-polling-signal';
import type { ColumnMapping, ValidationError } from '@/lib/excel';
import { toast, useExcel, useExcelProcessor } from '@/hooks';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useAuth } from '@/contexts/auth-context';
import { ExcelConfig } from '../utils';

interface UseHoaDonMuaHangTrongNuocImportProps {
  closeImportDialog: () => void;
  refreshData: () => Promise<void>;
  transactionOptions: { value: string; label: string }[];
}

export const useHoaDonMuaHangTrongNuocImport = ({
  closeImportDialog,
  refreshData,
  transactionOptions
}: UseHoaDonMuaHangTrongNuocImportProps) => {
  const [importErrorData, setImportErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const { entityUnit, entity } = useAuth();
  const { getUUIDByCode } = useExcel();
  const { processFile } = useExcelProcessor(ExcelConfig);
  const getTaskPollingSignal = useTaskPollingSignal();

  const handleImportBodies = useCallback(
    async (bodies: MuaHangRecord[]) => {
      const batchSize = 100;
      let totalCreated = 0;
      let totalFailed = 0;
      let hasError = false;

      setIsImporting(true);

      try {
        for (let index = 0; index < bodies.length; index += batchSize) {
          const batch = bodies.slice(index, index + batchSize);
          let responseData: any;

          try {
            const response = await postErpAsyncTask<any>(
              `/entities/${entity?.slug}/erp/${QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC}/bulk-create/`,
              {
                list: batch
              }
            );
            responseData = response.data;
          } catch (error) {
            console.error('Bulk create error:', error);
            totalFailed += batch.length;
            hasError = true;
            throw error;
          }

          if (isErpTaskSubmissionResponse(responseData)) {
            if (!entity?.slug) {
              throw new Error('Không tìm thấy thông tin công ty để kiểm tra tác vụ nhập Excel.');
            }
            const [taskResult] = await waitForErpTasks<MuaHangRecord>({
              entitySlug: entity.slug,
              tasks: [responseData],
              intervalMs: 2000,
              signal: getTaskPollingSignal()
            });
            const result = taskResult.result;

            if (result && typeof result === 'object' && 'success' in result) {
              totalCreated += result.created ?? 0;
              totalFailed += result.failed ?? 0;

              if (!result.success) {
                hasError = true;
              }
            } else {
              totalCreated += 1;
            }
            continue;
          }

          if (responseData && typeof responseData === 'object' && 'success' in responseData) {
            totalCreated += responseData.created ?? 0;
            totalFailed += responseData.failed ?? 0;

            if (!responseData.success) {
              hasError = true;
            }
          } else {
            totalCreated += Array.isArray(responseData) ? responseData.length : 0;
          }
        }

        if (hasError && totalCreated === 0) {
          toast({
            description: 'Không thể nhập hóa đơn. Vui lòng kiểm tra dữ liệu và thử lại.',
            variant: 'destructive'
          });
        } else {
          toast({
            description:
              totalFailed === 0
                ? 'Đã nhập hóa đơn từ Excel thành công'
                : 'Đã nhập hóa đơn từ Excel, một số chứng từ có lỗi',
            variant: 'default'
          });
        }
      } catch (error) {
        if (isTaskPollingCancelledError(error)) return;
        console.error('Error importing Excel:', error);
        toast({
          description: error instanceof Error ? error.message : 'Có lỗi xảy ra khi nhập dữ liệu Excel',
          variant: 'destructive'
        });
      } finally {
        await refreshData();
        setIsImporting(false);
      }
    },
    [entity?.slug, getTaskPollingSignal, refreshData]
  );

  const handleImportFromExcel = useCallback(
    async (data: { errors?: string; file: File | null; sheetIndex?: string }) => {
      setIsImporting(true);

      if (!entityUnit?.uuid) {
        toast({
          description: 'Không tìm thấy thông tin đơn vị',
          variant: 'destructive'
        });
        setIsImporting(false);
        return;
      }

      if (!data.file) {
        toast({
          description: 'Vui lòng chọn file Excel',
          variant: 'destructive'
        });
        setIsImporting(false);
        return;
      }

      const maxErrors = parseInt(data.errors || '200', 10);
      const sheetIndex = parseInt(data.sheetIndex || '0', 10);

      const result = await processFile({
        file: data.file,
        sheetIndex,
        maxErrors,
        getUUIDByCode,
        context: {
          entityUnitUUID: entityUnit.uuid,
          defaultMaGD: transactionOptions[0]?.value || 'HD'
        }
      });

      if (result.success && result.data) {
        await handleImportBodies(result.data);
        closeImportDialog();
        return;
      }

      if (result.errors && result.columnMapping) {
        setImportErrorData({ errors: result.errors, columnMapping: result.columnMapping });
        closeImportDialog();
      }

      setIsImporting(false);
    },
    [closeImportDialog, entityUnit?.uuid, getUUIDByCode, handleImportBodies, processFile, transactionOptions]
  );

  return {
    isImporting,
    handleImportFromExcel,
    importErrorDialog: {
      open: Boolean(importErrorData),
      errorData: importErrorData,
      onClose: () => setImportErrorData(null)
    }
  };
};
